/**
 * Growth Calculator Card — projects compound growth with animated AreaChart
 * and milestone dots at year 1/5/10/15/20/25.
 */
import React from 'react';
import { Calculator } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import InstitutionRecommendations from './InstitutionRecommendations';

const CHART_LABEL_STYLE = { value: 'Meses', position: 'insideBottom', offset: -5, fill: '#888' };
const TOOLTIP_CONTENT_STYLE = { backgroundColor: '#0a0a0a', border: '1px solid #39FF14', borderRadius: '8px' };
const MILESTONE_MONTHS = [12, 60, 120, 180, 240, 300];

const GrowthCalculatorCard = ({ calcData, setCalcData, calculateGrowth, futureValue, chartData, scrollToSection }) => (
  <Card className="tool-card span-2">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Calculator className="w-6 h-6 text-neon" />
        Calculadora de Crecimiento
      </CardTitle>
      <CardDescription>El interés compuesto: ese amigo silencioso que trabaja por ti mientras tú haces scroll en TikTok 💰</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="calc-grid">
        <div className="calc-inputs">
          <div>
            <label className="input-label">Aporte mensual ($)</label>
            <Input
              type="number"
              placeholder="1000"
              value={calcData.amount}
              onChange={(e) => setCalcData({ ...calcData, amount: e.target.value })}
              className="input-field"
              data-testid="growth-amount-input"
            />
          </div>
          <div>
            <label className="input-label">Años</label>
            <Input
              type="number"
              placeholder="10"
              value={calcData.years}
              onChange={(e) => setCalcData({ ...calcData, years: e.target.value })}
              className="input-field"
              data-testid="growth-years-input"
            />
          </div>
          <div>
            <label className="input-label">Rendimiento anual (%)</label>
            <Input
              type="number"
              placeholder="8"
              value={calcData.rate}
              onChange={(e) => setCalcData({ ...calcData, rate: e.target.value })}
              className="input-field"
              data-testid="growth-rate-input"
            />
          </div>
          <Button onClick={calculateGrowth} className="button-neon" data-testid="growth-calculate">
            Muéstrame la magia
          </Button>
          {futureValue && (
            <div className="future-value">
              <p className="text-sm text-gray-400">Tu futuro tú te dice "gracias"</p>
              <p className="text-4xl font-bold text-neon">
                ${Math.round(futureValue).toLocaleString()}
              </p>
            </div>
          )}
        </div>
        <div className="calc-chart">
          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#39FF14" stopOpacity={0.55} />
                    <stop offset="60%" stopColor="#39FF14" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#39FF14" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="month" stroke="#888" label={CHART_LABEL_STYLE} tick={{ fontSize: 11 }} />
                <YAxis
                  stroke="#888"
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  formatter={(value) => [`$${Math.round(value).toLocaleString()}`, 'Valor']}
                  labelFormatter={(label) => `Mes ${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#39FF14"
                  strokeWidth={3}
                  fill="url(#growthGradient)"
                  isAnimationActive
                  animationDuration={1800}
                  animationEasing="ease-out"
                  dot={false}
                  activeDot={{ r: 6, fill: '#39FF14', stroke: '#000', strokeWidth: 2 }}
                />
                {MILESTONE_MONTHS
                  .map((targetMonth) => chartData.find((d) => d.month === targetMonth))
                  .filter(Boolean)
                  .map((d) => (
                    <ReferenceDot
                      key={d.month}
                      x={d.month}
                      y={d.value}
                      r={6}
                      fill="#FFB800"
                      stroke="#000"
                      strokeWidth={2}
                      isFront
                    />
                  ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      {futureValue && calcData.years && (
        <InstitutionRecommendations
          years={parseInt(calcData.years)}
          source="growth"
          onCtaClick={() => scrollToSection('email-capture')}
        />
      )}
    </CardContent>
  </Card>
);

export default GrowthCalculatorCard;
